import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { LiveCostingEstateDetail } from "@shared-core/data/manufacturing-setup-types";
import { productTypeLabel } from "@shared-core/domain/cost-engine-validation-presentation";
import { formatDateTimeOrDash } from "@shared-core/domain/formatters";
import { paths } from "@shared-core/routing/routes";
import { Badge } from "../app/components/primitives/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../app/components/primitives/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../app/components/primitives/table";
import { CostCheckCell } from "../app/components/domain/catalogue/CostCheckBadges";
import { AlertTriangle, Factory } from "lucide-react";

/**
 * Settings › Manufacturing › Live costing (design note
 * `docs/features/cost-engine-productionisation/03_manufacturing_surfaces_design.md`
 * §"Live costing, all items"): the item list with each item's cost-check
 * state.
 *
 * "Needs attention" is derived — not a separate data model — from the same
 * item rows the dictionary index already carries: a row with a
 * `mismatchingCostLine` is a "Cost line mismatching" alert. See this pass's
 * own note on why this alert kind lives here rather than the customer-scoped
 * Alerts surface — that surface's `Alert` type is a real, backend-generated,
 * customer-scoped contract (`customer_id` required, a closed `alert_family`
 * enum tied to `customer_daily_metrics` signals) that structurally cannot host
 * an item/cost-line-scoped cost-engine condition without a backend change.
 */
export function LiveCostingStatusPage() {
  const adapter = useDataAdapter();
  const [detail, setDetail] = useState<LiveCostingEstateDetail | null>(null);
  const [loadError, setLoadError] = useState(false);

  const load = useCallback(() => {
    setLoadError(false);
    adapter
      .getLiveCostingEstate()
      .then(setDetail)
      .catch(() => setLoadError(true));
  }, [adapter]);

  useEffect(() => {
    load();
  }, [load]);

  if (loadError) {
    return (
      <div className="space-y-2">
        <PageHeading />
        <p className="rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-8 text-center text-sm text-destructive">
          Couldn&rsquo;t load Live costing. Please try again.
        </p>
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="space-y-2">
        <PageHeading />
        <p className="rounded-lg border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground">
          Loading…
        </p>
      </div>
    );
  }

  const mismatching = detail.items.filter((i) => i.mismatchingCostLine);

  return (
    <div className="space-y-6">
      <PageHeading />

      {mismatching.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="size-4 text-amber-600 dark:text-amber-400" />
              Needs attention
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {mismatching.map((item) => (
              <div
                key={`mismatch-${item.dictionaryItemId}`}
                className="flex flex-wrap items-center justify-between gap-2 text-sm"
              >
                <span>
                  <Badge variant="error" className="mr-2">
                    Cost line mismatching
                  </Badge>
                  <span className="font-medium">{item.name}</span> ·{" "}
                  {item.mismatchingCostLine?.label}
                </span>
                <Link
                  to={paths.settingsLiveCostingCostLine({
                    costCentreId: item.mismatchingCostLine!.costCentreId,
                    component: item.mismatchingCostLine!.component,
                  })}
                  className="text-[color:var(--color-primary)] hover:underline"
                >
                  Open cost line
                </Link>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Items</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="border-t border-border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Item</TableHead>
                  <TableHead>Product type</TableHead>
                  <TableHead>Cost check</TableHead>
                  <TableHead>Last checked</TableHead>
                  <TableHead>Rolling checks (4 weeks)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {detail.items.map((item) => (
                  <TableRow key={item.dictionaryItemId}>
                    <TableCell className="font-medium">
                      <Link
                        to={paths.dictionaryItemAdmin({
                          dictionaryItemId: item.dictionaryItemId,
                        })}
                        className="hover:underline"
                      >
                        {item.name}
                      </Link>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {productTypeLabel(
                        item.productTypeId,
                        item.productTypeName,
                      )}
                    </TableCell>
                    <TableCell>
                      <CostCheckCell
                        state={item.costCheckState}
                        mismatchingCostLine={item.mismatchingCostLine}
                        lastCheck={item.lastCheck}
                      />
                    </TableCell>
                    <TableCell className="text-muted-foreground text-xs">
                      {formatDateTimeOrDash(item.lastCheckedAt)}
                    </TableCell>
                    <TableCell
                      className={`tabular-nums text-xs ${item.rollingChecksCount === 0 ? "text-destructive font-medium" : "text-muted-foreground"}`}
                    >
                      {item.rollingChecksCount}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function PageHeading() {
  return (
    <div>
      <h1 className="text-2xl font-semibold text-foreground flex items-center gap-2">
        <Factory className="h-6 w-6" /> Status
      </h1>
      <p className="text-sm text-muted-foreground mt-1">
        Every item&rsquo;s cost check against Tharstern.
      </p>
    </div>
  );
}
